/**
 * S2P Review Queue — Phase 2.B Step 8.A
 *
 * Tenant-wide page for PMs to triage Autonomous Procurement Agent
 * decisions. Pulls from:
 *   GET  /api/pece/s2p/suggestions  (review queue)
 *   GET  /api/pece/s2p/decisions    (recent history filter)
 *   POST /api/pece/s2p/run          (manual scan trigger)
 *   POST /api/pece/s2p/decisions/{id}/approve
 *   POST /api/pece/s2p/decisions/{id}/dismiss
 */
import React, { useEffect, useState } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  Brain, RefreshCw, Loader2, ListChecks, Wand2, X, Check, AlertTriangle,
  Activity, ChevronRight, Sparkles, Filter, Play, Gauge,
} from 'lucide-react';

const TIER_META = {
  auto_execute:     { label: 'Auto Executed', emoji: '🟢', cls: 'bg-emerald-50 text-emerald-700 border-emerald-300' },
  draft_for_review: { label: 'Draft — Review', emoji: '🟡', cls: 'bg-amber-50 text-amber-700 border-amber-300' },
  suggest_only:     { label: 'Suggestion',     emoji: '🟠', cls: 'bg-orange-50 text-orange-700 border-orange-300' },
  log_only:         { label: 'Log Only',       emoji: '🔵', cls: 'bg-sky-50 text-sky-700 border-sky-300' },
  blocked:          { label: 'Blocked',        emoji: '🔴', cls: 'bg-rose-50 text-rose-700 border-rose-300' },
};

const fmtConfidence = (c) => `${Math.round((c || 0) * 100)}%`;
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const S2PReviewQueue = () => {
  const [tab, setTab] = useState('queue');
  const [queue, setQueue] = useState([]);
  const [decisions, setDecisions] = useState([]);
  const [filterTier, setFilterTier] = useState('');
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [actingOn, setActingOn] = useState(null);
  const [dismissModal, setDismissModal] = useState(null);

  const loadData = async () => {
    setLoading(true);
    try {
      const [q, d] = await Promise.all([
        axiosInstance.get(`/pece/s2p/suggestions?limit=50`),
        axiosInstance.get(`/pece/s2p/decisions?limit=200${filterTier ? `&tier=${filterTier}` : ''}`),
      ]);
      setQueue(q.data);
      setDecisions(d.data);
    } catch (e) {
      toast.error('فشل تحميل بيانات S2P');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); /* eslint-disable-next-line */ }, [filterTier]);

  const runScan = async () => {
    setRunning(true);
    try {
      const r = await axiosInstance.post(`/pece/s2p/run`, { lookahead_days: 30, dry_run: false });
      const s = r.data.stats || {};
      toast.success(
        `تم المسح: ${s.activities_scanned} نشاط · ${s.decisions_total} قرار · ${s.pprs_auto_created} طلب تلقائي`
      );
      await loadData();
    } catch (e) {
      toast.error('فشل تشغيل المسح');
    } finally {
      setRunning(false);
    }
  };

  const approve = async (decision) => {
    setActingOn(decision.id);
    try {
      const r = await axiosInstance.post(`/pece/s2p/decisions/${decision.id}/approve`);
      toast.success(`تم اعتماد القرار → PPR ${r.data.ppr.ppr_no}`);
      await loadData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فشل الاعتماد');
    } finally {
      setActingOn(null);
    }
  };

  const dismiss = async (id, reason) => {
    setActingOn(id);
    try {
      await axiosInstance.post(`/pece/s2p/decisions/${id}/dismiss`, { reason });
      toast.success('تم تجاهل القرار');
      setDismissModal(null);
      await loadData();
    } catch (e) {
      toast.error('فشل التجاهل');
    } finally {
      setActingOn(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="s2p-review-page">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs text-indigo-600 font-bold uppercase tracking-wider mb-1 flex items-center gap-2">
                <Brain className="w-4 h-4" /> S2P · Autonomous Procurement Agent
              </div>
              <h1 className="text-2xl font-bold text-gray-900">قائمة مراجعة الذكاء التلقائي</h1>
              <p className="text-sm text-gray-500 mt-1">
                يستعرض القرارات التي تحتاج مراجعة (Draft / Suggestion) من Agent الذكي.
              </p>
            </div>
            <div className="flex items-center gap-2">
            <button
              data-testid="s2p-run-btn"
              onClick={runScan}
              disabled={running}
              className="px-5 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-indigo-400 flex items-center gap-2 font-semibold"
            >
              {running ? <Loader2 className="w-5 h-5 animate-spin" /> : <Play className="w-5 h-5" />}
              تشغيل المسح الآن
            </button>
            <a
              data-testid="s2p-dashboard-link"
              href="/pece/s2p/dashboard"
              className="px-5 py-3 border border-indigo-300 text-indigo-700 rounded-lg hover:bg-indigo-50 flex items-center gap-2 font-semibold"
            >
              <Gauge className="w-5 h-5" />
              لوحة الفعالية
            </a>
            </div>
          </div>

          <div className="flex gap-1 mt-5">
            <TabBtn id="queue" active={tab} onClick={setTab} icon={<ListChecks className="w-4 h-4" />}>
              قائمة المراجعة <span className="ml-2 bg-indigo-100 text-indigo-700 text-xs px-2 py-0.5 rounded-full">{queue.length}</span>
            </TabBtn>
            <TabBtn id="all" active={tab} onClick={setTab} icon={<Activity className="w-4 h-4" />}>
              كل القرارات
            </TabBtn>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        {loading && (
          <div className="flex items-center justify-center py-20 text-gray-500" data-testid="s2p-loading">
            <Loader2 className="w-6 h-6 animate-spin mr-2" /> جاري التحميل...
          </div>
        )}

        {!loading && tab === 'queue' && (
          <ReviewQueueList
            queue={queue}
            actingOn={actingOn}
            onApprove={approve}
            onDismiss={(d) => setDismissModal(d)}
          />
        )}

        {!loading && tab === 'all' && (
          <AllDecisionsList
            decisions={decisions}
            filterTier={filterTier}
            setFilterTier={setFilterTier}
          />
        )}
      </div>

      {dismissModal && (
        <DismissModal
          decision={dismissModal}
          onCancel={() => setDismissModal(null)}
          onConfirm={(reason) => dismiss(dismissModal.id, reason)}
          isActing={actingOn === dismissModal.id}
        />
      )}
    </div>
  );
};

const TabBtn = ({ id, active, onClick, children, icon }) => (
  <button
    data-testid={`s2p-tab-${id}`}
    onClick={() => onClick(id)}
    className={`px-4 py-2.5 text-sm font-medium border-b-2 transition flex items-center gap-2 ${
      active === id
        ? 'border-indigo-600 text-indigo-700'
        : 'border-transparent text-gray-600 hover:text-gray-900'
    }`}
  >
    {icon}{children}
  </button>
);

// ─── Review Queue ─────────────────────────────────────────────────────────

const ReviewQueueList = ({ queue, actingOn, onApprove, onDismiss }) => {
  if (queue.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-dashed border-gray-300 p-12 text-center" data-testid="s2p-empty-queue">
        <Sparkles className="w-12 h-12 mx-auto text-emerald-500 mb-3" />
        <h3 className="text-lg font-bold text-gray-800">لا توجد قرارات قيد المراجعة</h3>
        <p className="text-sm text-gray-500 mt-1">
          إما لا يوجد ما يستحق الاهتمام حالياً، أو لم يتم تشغيل أي مسح بعد. اضغط <strong>تشغيل المسح</strong> أعلاه.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="s2p-queue-list">
      {queue.map((d) => (
        <DecisionCard
          key={d.id}
          decision={d}
          isActing={actingOn === d.id}
          onApprove={() => onApprove(d)}
          onDismiss={() => onDismiss(d)}
          showActions
        />
      ))}
    </div>
  );
};

const DecisionCard = ({ decision, isActing, onApprove, onDismiss, showActions }) => {
  const meta = TIER_META[decision.tier] || TIER_META.log_only;
  return (
    <div
      data-testid={`s2p-decision-${decision.id}`}
      className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-sm transition"
    >
      <div className="flex items-start gap-4">
        <div className={`px-3 py-2 rounded-lg border ${meta.cls} text-center min-w-[100px]`}>
          <div className="text-2xl">{meta.emoji}</div>
          <div className="text-[10px] uppercase font-bold tracking-wider mt-1">{meta.label}</div>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="text-xs text-gray-500 mb-0.5">{decision.project_name || decision.project_id}</div>
              <div className="font-mono font-bold text-sm">{decision.activity_code}</div>
              <div className="text-xs text-gray-600">{decision.activity_name || '—'}</div>
            </div>
            <div className="flex items-center gap-3">
              <Stat label="PRS" v={decision.prs?.toFixed(0)} />
              <Stat label="Confidence" v={fmtConfidence(decision.confidence)} />
            </div>
          </div>

          {/* Suggested Action */}
          {decision.action && (
            <div className="mt-3 p-3 bg-indigo-50 border border-indigo-200 rounded-lg flex items-center gap-3">
              <Wand2 className="w-4 h-4 text-indigo-600" />
              <div className="flex-1 text-sm">
                <span className="font-semibold">{decision.action.text}</span>
                <span className="text-xs text-indigo-700 ml-2">
                  · urgency: {decision.action.urgency}
                </span>
              </div>
            </div>
          )}

          {/* Blockers */}
          {decision.blockers && decision.blockers.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {decision.blockers.map((b) => (
                <span key={b} className="text-[10px] px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 border border-rose-200">
                  ⛔ {b}
                </span>
              ))}
            </div>
          )}

          {/* PPR linkage */}
          {decision.ppr_no && (
            <div className="mt-2 text-xs text-emerald-700">
              ✓ PPR: <span className="font-bold font-mono">{decision.ppr_no}</span>
            </div>
          )}

          <div className="mt-3 text-[10px] text-gray-400">
            {fmtDate(decision.created_at)} · status: <span className="font-bold">{decision.status}</span>
          </div>
        </div>

        {showActions && decision.status === 'pending' && (
          <div className="flex flex-col gap-2 min-w-[110px]">
            <button
              data-testid={`s2p-approve-${decision.id}`}
              disabled={isActing}
              onClick={onApprove}
              className="px-3 py-2 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50 flex items-center justify-center gap-1"
            >
              {isActing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              اعتماد
            </button>
            <button
              data-testid={`s2p-dismiss-${decision.id}`}
              disabled={isActing}
              onClick={onDismiss}
              className="px-3 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-50 disabled:opacity-50 flex items-center justify-center gap-1"
            >
              <X className="w-4 h-4" /> تجاهل
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const Stat = ({ label, v }) => (
  <div className="text-center">
    <div className="text-[10px] uppercase text-gray-500 font-semibold">{label}</div>
    <div className="font-bold text-lg">{v}</div>
  </div>
);

// ─── All Decisions Tab ────────────────────────────────────────────────────

const AllDecisionsList = ({ decisions, filterTier, setFilterTier }) => (
  <div>
    <div className="flex items-center gap-3 mb-4">
      <Filter className="w-4 h-4 text-gray-500" />
      <select
        data-testid="s2p-filter-tier"
        value={filterTier}
        onChange={(e) => setFilterTier(e.target.value)}
        className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
      >
        <option value="">كل الـ tiers</option>
        {Object.entries(TIER_META).map(([k, v]) => (
          <option key={k} value={k}>{v.emoji} {v.label}</option>
        ))}
      </select>
      <span className="text-sm text-gray-500">عدد القرارات: <strong>{decisions.length}</strong></span>
    </div>

    {decisions.length === 0 ? (
      <div className="bg-white rounded-xl border border-dashed border-gray-300 p-12 text-center text-gray-500">
        لا توجد قرارات بعد. شغّل المسح من الزر أعلاه.
      </div>
    ) : (
      <div className="space-y-3" data-testid="s2p-all-list">
        {decisions.map((d) => (
          <DecisionCard key={d.id} decision={d} showActions={false} />
        ))}
      </div>
    )}
  </div>
);

// ─── Dismiss Modal ────────────────────────────────────────────────────────

const DismissModal = ({ decision, onCancel, onConfirm, isActing }) => {
  const [reason, setReason] = useState('');
  return (
    <div
      data-testid="s2p-dismiss-modal"
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-xl w-full max-w-md p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-bold mb-1">تأكيد التجاهل</h3>
        <p className="text-sm text-gray-600 mb-4">
          سيتم تجاهل القرار للنشاط <span className="font-mono font-bold">{decision.activity_code}</span>.
        </p>
        <textarea
          data-testid="s2p-dismiss-reason"
          placeholder="السبب (اختياري)..."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-4 h-24 resize-none"
        />
        <div className="flex justify-end gap-2">
          <button
            data-testid="s2p-dismiss-cancel"
            onClick={onCancel}
            disabled={isActing}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
          >
            إلغاء
          </button>
          <button
            data-testid="s2p-dismiss-confirm"
            onClick={() => onConfirm(reason)}
            disabled={isActing}
            className="px-4 py-2 bg-rose-600 text-white rounded-lg text-sm font-semibold hover:bg-rose-700 disabled:opacity-50 flex items-center gap-1"
          >
            {isActing ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
            تأكيد التجاهل
          </button>
        </div>
      </div>
    </div>
  );
};

export default S2PReviewQueue;
